import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { defaultMenu } from './data/menu';
import { ui } from './i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppContext = createContext(null);

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('ponti_lang') || 'it');
  const [menu, setMenu] = useState(defaultMenu);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    localStorage.setItem('ponti_lang', lang);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const refreshMenu = useCallback(async () => {
    try {
      const res = await fetch(`${API}/menu`);
      if (res.ok) {
        const data = await res.json();
        // Only use backend data if it looks populated; otherwise keep defaultMenu
        if (data && Array.isArray(data.mainCategories) && data.mainCategories.length > 0) {
          setMenu(data);
        }
      }
    } catch (err) {
      console.warn('Menu fetch failed, using default', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMenu();
  }, [refreshMenu]);

  // Check if we already have a valid admin session cookie on mount
  useEffect(() => {
    let active = true;
    fetch(`${API}/admin/session`, { credentials: 'include' })
      .then((res) => { if (active) setIsAdmin(res.ok); })
      .catch(() => { if (active) setIsAdmin(false); })
      .finally(() => { if (active) setAuthChecked(true); });
    return () => { active = false; };
  }, []);

  const login = useCallback(async (password) => {
    const res = await fetch(`${API}/admin/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) throw new Error('Password non valida');
    await res.json();
    setIsAdmin(true);
    return true;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/admin/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.warn('Logout request failed:', e);
    }
    setIsAdmin(false);
  }, []);

  const saveMenu = useCallback(async (newMenu) => {
    const res = await fetch(`${API}/menu`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newMenu),
    });
    if (res.status === 401) {
      setIsAdmin(false);
      throw new Error('Sessione scaduta, effettua di nuovo il login');
    }
    if (!res.ok) throw new Error('Errore nel salvataggio');
    const data = await res.json();
    setMenu(data);
    return data;
  }, []);

  const t = useCallback(
    (key) => (ui[lang] && ui[lang][key]) || ui.it[key] || key,
    [lang]
  );

  const track = useCallback(async (type, ref, label) => {
    try {
      await fetch(`${API}/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, ref: ref || '', label: label || '' }),
        keepalive: true,
      });
    } catch (err) {
      // analytics is best-effort; log at debug level to help diagnose, never alert the user.
      if (process.env.NODE_ENV !== 'production') console.debug('track event failed:', err);
    }
  }, []);

  const contextValue = useMemo(
    () => ({ lang, setLang, menu, loading, isAdmin, authChecked, login, logout, saveMenu, refreshMenu, t, track, API }),
    [lang, menu, loading, isAdmin, authChecked, login, logout, saveMenu, refreshMenu, t, track]
  );

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};
